import React from 'react';
import { useSelector } from 'react-redux';
import { Translate } from 'react-redux-i18n';

import { ViewSectionRow } from '../../../components/layouts/viewSectionRow';
import { ViewTable } from '../../../components/layouts/viewTable';
import { isArrayWithItems } from '../../../helpers/arrayHelper';
import { toCurrency } from '../../../helpers/converterHelper';
import { RootState } from '../../../store';
import { FormBuilderSectionRow } from '../../../components/layouts/formBuilderSectionRow';
import { LinkedEntities } from './LinkedEntities';
import { entityMap } from '../store/ticketingActions';
import { pageTypesStrings } from '../../../constants/pageTypes';
import { displayAddress } from '../../../helpers/templateHelper';

import CurrencyHelper from '../../../helpers/currencyHelper';
import { formatDateWithPattern } from '../../../utils/time/formatDateWithPattern';
import config from '../../../constants';
import { SystemOnlyDetail } from '../../../components/common/SystemOnlyBadge';

interface ITicketInformationTableProps {
  showEmptyFields: boolean;
  displayOrgCurrency: boolean;
  isSystemAdmin: boolean;
}

const TicketInformation = ({ details, index, showEmptyFields, currency, isSystemAdmin }: any) => {
  const ticketing = useSelector((state: RootState) => state.ticketing);
  const address = details.customer?.address;
  const relatedData = ticketing.relatedRecord?.data?.data;

  const buildEntities = () => {
    if (relatedData?.fundraisers && details.id) {
      const campaign = relatedData?.campaign;
      const campaignFunraisers = relatedData.fundraisers.filter(
        (x: any) => x.saleDetailId === details.id
      );
      const tmp = entityMap(
        campaignFunraisers,
        pageTypesStrings.FUNDRAISER,
        campaign
      );
      return tmp;
    }
    return [];
  };

  return (
    <div style={{ marginTop: 15 }}>
      <ViewTable
        key={index}
        title={
          <>
            <Translate value="ticketing.edit.ticket.header" index={index + 1} />
            {isSystemAdmin && (
              <SystemOnlyDetail>
                <Translate
                  value="ticketing.edit.ticket.header-sysadmin"
                  saleDetailId={details.id}
                  ticketId={details.ticketId}
                />
              </SystemOnlyDetail>
            )}
          </>
        }
      >
        <ViewSectionRow
          isShowEmptyFields={showEmptyFields}
          title="ticketing.edit.ticket.ticket.label"
          data={
            <>
              {details.ticket?.name} ({CurrencyHelper.formatCurrency(details.price, currency)})
            </>
          }
        />
        <ViewSectionRow
          isShowEmptyFields={showEmptyFields}
          title="ticketing.edit.ticket.first-name.label"
          data={details.customer?.firstName}
        />
        <ViewSectionRow
          isShowEmptyFields={showEmptyFields}
          title="ticketing.edit.ticket.last-name.label"
          data={details.customer?.lastName}
        />
        <ViewSectionRow
          isShowEmptyFields={showEmptyFields}
          title="ticketing.edit.ticket.email.label"
          data={details.customer?.email}
        />
        <ViewSectionRow
          isShowEmptyFields={showEmptyFields}
          title="ticketing.edit.ticket.address.label"
          data={displayAddress(address)}
        />
        <ViewSectionRow
          isShowEmptyFields={showEmptyFields}
          title="ticketing.edit.ticket.phone-number.label"
          data={details.customer?.phoneNumber}
        />
        {details?.checkedIn && details?.checkedInAtLocal && (
          <ViewSectionRow
            isShowEmptyFields={showEmptyFields}
            title="ticketing.edit.ticket.checked-in.label"
            data={`Yes, at ${formatDateWithPattern(details.checkedInAtLocal, config.INVERSION_FULL_DATE_TIME_PERIODS_FORMAT)}`}
          />
        )}
        {isArrayWithItems(details.fields) &&
          React.Children.toArray(
            details.fields.map((field: any) => (
              <FormBuilderSectionRow
                isShowEmptyFields={showEmptyFields}
                field={field}
              />
            ))
          )}
        <LinkedEntities entities={buildEntities()} />
      </ViewTable>
    </div>
  );
};

export const TicketInformationTable = ({
  showEmptyFields,
  displayOrgCurrency,
  isSystemAdmin
}: ITicketInformationTableProps) => {
  const ticketing = useSelector((state: RootState) => state.ticketing);
  const recordData = ticketing.record?.data?.data || {};
  const details: any[] = recordData?.details || [];
  const tickets = details.filter(item => item.ticketId);
  return (
    <>
      {tickets?.map((details: any, index: number) => (
        <TicketInformation
          key={index}
          details={
            displayOrgCurrency
              ? { ...details, price: details.price * recordData.financials.exchangeRate }
              : details
          }
          index={index}
          showEmptyFields={showEmptyFields}
          currency={displayOrgCurrency ? recordData?.organization.currency : recordData?.currency}
          isSystemAdmin={isSystemAdmin}
        />
      ))}
    </>
  );
};
